import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SiteContent {
  id: string;
  key: string;
  value: Record<string, any>;
  updated_at: string;
}

// Fetch single content by key
export function useSiteContent(key: string) {
  return useQuery({
    queryKey: ["site-content", key],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("site_content")
        .select("*")
        .eq("key", key)
        .maybeSingle();
      
      if (error) throw error;
      return data as SiteContent | null;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

// Fetch multiple content items by keys
export function useSiteContents(keys: string[]) {
  return useQuery({
    queryKey: ["site-content", keys],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("site_content")
        .select("*")
        .in("key", keys);
      
      if (error) throw error;
      
      // Convert to a map for easy access
      const contentMap: Record<string, Record<string, any>> = {};
      data?.forEach((item: SiteContent) => {
        contentMap[item.key] = item.value;
      });
      return contentMap;
    },
    staleTime: 1000 * 60 * 5,
  });
}

// Mutation to update content (Admin only)
export function useUpdateSiteContent() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: Record<string, any> }) => {
      const { data, error } = await (supabase as any)
        .from("site_content")
        .upsert({ 
          key, 
          value,
          updated_at: new Date().toISOString()
        }, { 
          onConflict: 'key' 
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["site-content", variables.key] });
      queryClient.invalidateQueries({ queryKey: ["site-content"] });
    },
  });
}

// Default content fallbacks (used when DB content is not available)
export const DEFAULT_CONTENT = {
  homepage_hero: {
    title: "Woven Heritage",
    subtitle: "Discover timeless elegance in every thread. Handcrafted sarees that celebrate tradition.",
    cta_text: "Explore Collection",
    cta_link: "/shop",
    image: "/images/hero/hero-saree.jpg"
  },
  homepage_manifesto: {
    title: "Our Manifesto",
    quote: "Each thread tells a story of tradition, each weave carries the soul of its maker. We believe in preserving the art of handloom while embracing the modern woman.",
    image: "/images/heritage/manifesto-weaving.jpg"
  },
  homepage_newsletter: {
    title: "Stay Connected",
    subtitle: "Receive exclusive offers, styling tips, and early access to new collections."
  },
  about_hero: {
    title: "Our Story",
    subtitle: "A journey of passion, tradition, and timeless craftsmanship",
    image: "/images/about/hero-heritage.jpg"
  },
  about_story: {
    title: "The Leestone Legacy",
    content: "Founded with a vision to preserve India's rich textile heritage, Leestone brings you authentic handcrafted sarees from master weavers across the country.",
    image: "/images/heritage/manifesto-weaving.jpg"
  },
  about_mission: {
    title: "Our Mission",
    content: "To bridge the gap between traditional artisans and modern consumers, ensuring fair compensation for craftspeople while delivering exceptional quality to our customers."
  },
  homepage_instagram: {
    handle: "@leestone_45",
    title: "Visual Diary",
    subtitle: "Follow our journey and share your Leestone moment.",
    cta_text: "Follow Our Story",
    cta_link: "https://www.instagram.com/leestone_45/"
  },
  homepage_archives: {
    title: "The Heritage Archives",
    subtitle: "Exploring the intersection of ancestral craftsmanship and contemporary silhouettes."
  },
  about_stats: {
    stat1_value: "5000+",
    stat1_label: "Artisans Supported",
    stat2_value: "25+",
    stat2_label: "Textile Regions",
    stat3_value: "100k+",
    stat3_label: "Cherished Patrons",
    stat4_value: "38 Years",
    stat4_label: "Of Crafting Dreams",
    tagline: "Join us in preserving the art of Indian textiles for generations to come."
  },
  about_values: {
    title: "Our Eternal Values",
    subtitle: "Dedicated to excellence in every stitch, from sourcing to delivery.",
    value1_title: "Authentic Sourcing",
    value1_desc: "Direct collaborations with master weavers and registered cooperatives across Indian textile hubs.",
    value2_title: "Ethical Craft",
    value2_desc: "Championing fair wages and sustainable livelihoods for our artisans.",
    value3_title: "Unmatched Quality",
    value3_desc: "Every piece undergoes a multi-point inspection to ensure it meets our heritage standards."
  },
  homepage_features: {
    feature1_title: "Artisanal Mastery",
    feature1_desc: "Each thread woven by lineage craftsmen in our ancestral looms.",
    feature2_title: "Heritage Quality",
    feature2_desc: "Sourcing only the finest Grade-A silks and sustainable cottons.",
    feature3_title: "Varanasi Roots",
    feature3_desc: "Preserving the 500-year-old textile legacy of the holy ghats.",
    feature4_title: "Bespoke Care",
    feature4_desc: "A concierge service dedicated to fitting your unique silhouette."
  },
  site_contact: {
    email: "leestone496@gmail.com",
    email_sub: "For bespoke inquiries & orders",
    phone: "+91 77369 30840",
    phone_sub: "Mon-Sat, 10 AM - 7 PM IST",
    whatsapp: "+91 77369 30840",
    whatsapp_sub: "Direct chat with our stylists",
    address: "Chennai, Tamil Nadu",
    address_sub: "India",
    hours_weekday: "10:00 AM - 07:00 PM",
    hours_sunday: "11:00 AM - 05:00 PM",
    footer_address: "Leestone,\nChennai, Tamil Nadu, India"
  },
  homepage_instagram_images: {
    image1: "/images/heritage/social-grid.jpg",
    image2: "/images/heritage/collection-silks.jpg",
    image3: "/images/heritage/collection-bridal.jpg",
    image4: "/images/heritage/collection-suit.jpg",
    image5: "/images/heritage/hero-charcoal.jpg",
    image6: "/images/heritage/hero-modern.jpg"
  },
  page_privacy: {
    title: "Privacy Policy",
    last_updated: "January 2026",
    content: `At Leestone, we hold the privacy of our patrons in the highest regard. This Privacy Policy describes how your personal information is collected, used, and shared when you visit or make a purchase from leestonefashion.com.

## Information We Collect

When you visit the Site, we automatically collect certain information about your device, including information about your web browser, IP address, and time zone. Additionally, as you browse the Site, we collect information about the individual web pages or products that you view.

When you make a purchase or attempt to make a purchase through the Site, we collect certain information from you, including your name, billing address, shipping address, payment information, email address, and phone number.

## How We Use Your Information

We use the Order Information that we collect generally to fulfill any orders placed through the Site (including processing your payment information, arranging for shipping, and providing you with invoices and/or order confirmations).

## Contact

For more information about our privacy practices, if you have questions, or if you would like to make a complaint, please contact our concierge at leestone496@gmail.com.`
  },
  page_terms: {
    title: "Terms & Conditions",
    last_updated: "January 2026",
    content: `By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement.

## Intellectual Property

The Site and its original content, features, and functionality are owned by Leestone and are protected by international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.

## Accuracy of Materials

The materials appearing on Leestone's website could include technical, typographical, or photographic errors. Leestone does not warrant that any of the materials on its website are accurate, complete or current.

## Payment Terms

All payments for heritage pieces must be made through our authorized payment gateways. We reserve the right to cancel any order if we suspect fraudulent activity.`
  },
  page_returns: {
    title: "Returns & Exchanges",
    last_updated: "Our Commitment to Your Satisfaction",
    content: `We accept returns within 2 days of delivery for unused items in original packaging with tags intact. The product must be in its pristine condition, unworn and unwashed.

Please note that Customized or Altered items cannot be returned as they are crafted specifically to your measurements.

## How to Initiate a Return

1. Visit your Orders page in your account dashboard.
2. Select the relevant order and choose 'Request Return/Exchange'.
3. Specify the reason and upload images if the item is damaged.
4. Our concierge will review and arrange a complimentary pickup within 3-5 business days.

## Exchanges

We offer one complimentary exchange per order for size or color variations, subject to product availability.`
  },
  store_policies: {
    shipping_flat_rate: 50,
    gst_percentage: 5,
    return_days: 2
  }
};

// Helper to get content with fallback
export function getContentWithFallback(
  contentMap: Record<string, Record<string, any>> | undefined,
  key: keyof typeof DEFAULT_CONTENT
): Record<string, any> {
  return contentMap?.[key] || DEFAULT_CONTENT[key];
}
